import ClearIcon from '@mui/icons-material/Clear'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  Input,
  InputAdornment,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'
import { alpha } from '@mui/material/styles'
import {
  journeyJobStatusSelector,
  MANUAL_INTERVENTION_STATUS_LABEL,
} from 'api/models/Common/JyrneyEnums'
import useClients from 'hooks/useClients'
import { useEffect, useRef, useState } from 'react'

type ToolbarProps = {
  selectedStatuses: string[]
  SetSelectedStatuses: (s: string[]) => void
  selectedJobTypes: string[]
  setSelectedJobTypes: (s: string[]) => void
  selectedClient: string
  setSelectedClient: (s: string) => void
  searchText: string
  setSearchText: (s: string) => void
  hideStaleManualIntervention: boolean
  setHideStaleManualIntervention: (s: boolean) => void
  staleManualInterventionCount: number
  isLoading?: boolean
  onCopyJobReferences: () => Promise<boolean>
  copyJobReferencesDisabled: boolean
  copyJobReferencesCount: number
}

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1

const CHIP_BUZZ_INTERVAL_MS = 0.05 * 60 * 1000
const JOB_TYPE_OPTIONS = [
  { value: 'Standard', label: 'Standard' },
  { value: 'Flexible', label: 'Flexible' },
  { value: 'DestinationSupplier', label: 'Destination Supplier' },
  { value: 'MeetAndGreet', label: 'Meet & Greet' },
]

const JOB_TYPE_LABELS: Record<string, string> = JOB_TYPE_OPTIONS.reduce(
  (accumulator, option) => {
    accumulator[option.value] = option.label
    return accumulator
  },
  {} as Record<string, string>,
)

const STATUS_OPTIONS: Array<[string, string]> = [
  ...Object.entries(journeyJobStatusSelector),
  ['manualIntervention', MANUAL_INTERVENTION_STATUS_LABEL],
]

const getToolbarPalette = (theme: any) => {
  const backgroundColor =
    theme?.components?.journeyStatus?.headerColour || theme.palette.grey[300]
  const foregroundColor =
    theme?.components?.journeyStatus?.headerTextColour ||
    theme.palette.getContrastText(backgroundColor)

  return {
    backgroundColor,
    foregroundColor,
    mutedForegroundColor: alpha(foregroundColor, 0.74),
    borderColor: alpha(foregroundColor, 0.46),
    surfaceColor: alpha(foregroundColor, 0.06),
  }
}

const Toolbar = ({
  selectedStatuses,
  SetSelectedStatuses,
  selectedJobTypes,
  setSelectedJobTypes,
  selectedClient,
  setSelectedClient,
  searchText,
  setSearchText,
  hideStaleManualIntervention,
  setHideStaleManualIntervention,
  staleManualInterventionCount,
  onCopyJobReferences,
  copyJobReferencesDisabled,
  copyJobReferencesCount,
}: ToolbarProps) => {
  const { options: clientOptions } = useClients()
  const [chipAnimationKey, setChipAnimationKey] = useState(0)
  const [copied, setCopied] = useState(false)
  const copyResetTimeoutRef = useRef<number | null>(null)

  const handleStatusSelectChange = (
    event: SelectChangeEvent<typeof selectedStatuses>,
  ) => {
    const {
      target: { value },
    } = event
    SetSelectedStatuses(typeof value === 'string' ? value.split(',') : value)
  }

  const handleClientSelectChange = (event: SelectChangeEvent) => {
    setSelectedClient(event.target.value as string)
  }

  const [clientSearchText, setClientSearchText] = useState('')

  useEffect(() => {
    if (!hideStaleManualIntervention) return

    const intervalId = window.setInterval(() => {
      setChipAnimationKey((currentKey) => currentKey + 1)
    }, CHIP_BUZZ_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [hideStaleManualIntervention, staleManualInterventionCount])

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!copied) return
    if (copyJobReferencesDisabled) {
      setCopied(false)
    }
  }, [copied, copyJobReferencesDisabled])

  const handleCopyJobReferences = async () => {
    const hasCopied = await onCopyJobReferences()
    if (!hasCopied) {
      return
    }
    setCopied(true)
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current)
    }
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopied(false)
      copyResetTimeoutRef.current = null
    }, 1800)
  }

  return (
    <>
      <Box
        component="div"
        sx={(theme) => {
          const {
            backgroundColor,
            foregroundColor,
            mutedForegroundColor,
            borderColor,
            surfaceColor,
          } = getToolbarPalette(theme)

          return {
            padding: { xs: '16px', lg: '18px 20px 16px' },
            backgroundColor,
            display: 'grid',
            gap: '16px',
            color: foregroundColor,
            '--booking-toolbar-text': foregroundColor,
            '--booking-toolbar-text-muted': mutedForegroundColor,
            '--booking-toolbar-border': borderColor,
            '--booking-toolbar-surface': surfaceColor,
          }
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '16px',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            alignItems: 'end',
          }}
        >
          <FormControl sx={{ flex: '1 1 280px', minWidth: '240px' }}>
            <Typography
              sx={{
                color: 'var(--booking-toolbar-text)',
                fontSize: '14px',
                marginBottom: '5px',
              }}
            >
              Status:
            </Typography>
            <Select
              key="statusSelect"
              id="statusSelect"
              multiple
              displayEmpty
              value={selectedStatuses}
              onChange={handleStatusSelectChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <Box
                      component="span"
                      sx={{
                        opacity: '0.5',
                      }}
                    >
                      Status Select
                    </Box>
                  )
                }

                return selected.join(', ')
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 'maxContent',
                  },
                },
              }}
              sx={{
                height: '36px',
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {STATUS_OPTIONS.map(([value, label]) => (
                <MenuItem key={value} value={label}>
                  <Checkbox checked={selectedStatuses.indexOf(label) > -1} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: '1 1 280px', minWidth: '240px' }}>
            <Typography
              sx={{
                color: 'var(--booking-toolbar-text)',
                fontSize: '14px',
                marginBottom: '5px',
              }}
            >
              Job Type:
            </Typography>
            <Select
              multiple
              value={selectedJobTypes}
              onChange={(e) => setSelectedJobTypes(e.target.value as string[])}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === JOB_TYPE_OPTIONS.length) {
                  return 'All'
                }

                return selected
                  .map(
                    (selectedValue) =>
                      JOB_TYPE_LABELS[selectedValue] ?? selectedValue,
                  )
                  .join(', ')
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 'maxContent',
                  },
                },
              }}
              sx={{
                height: '36px',
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {JOB_TYPE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={selectedJobTypes.includes(value)} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: '1 1 280px', minWidth: '240px' }}>
            <Typography
              sx={{
                color: 'var(--booking-toolbar-text)',
                fontSize: '14px',
                marginBottom: '5px',
              }}
            >
              Client:
            </Typography>
            <Select
              key="clientSelect"
              id="clientSelect"
              value={selectedClient}
              onChange={handleClientSelectChange}
              sx={{
                height: '36px',
                '& .MuiSelect-select': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <ListSubheader>
                <TextField
                  size="small"
                  placeholder="Type to search..."
                  fullWidth
                  value={clientSearchText}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setClientSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing (default Select behaviour)
                      e.stopPropagation()
                    }
                  }}
                />
              </ListSubheader>
              <MenuItem key={'All'} value={'All'}>
                All
              </MenuItem>
              {clientOptions
                ? Object.entries(clientOptions)
                    .filter(
                      ([, label]) =>
                        containsText(label.toString(), clientSearchText) ||
                        selectedClient === label,
                    )
                    .sort(([, labelA], [, labelB]) =>
                      labelA.toString().localeCompare(labelB.toString()),
                    )
                    .map(([value, label]) => {
                      return (
                        <MenuItem key={value} value={label}>
                          {label}
                        </MenuItem>
                      )
                    })
                : null}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-end',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={hideStaleManualIntervention}
                onChange={(event) =>
                  setHideStaleManualIntervention(event.target.checked)
                }
                color="default"
              />
            }
            label={
              <Typography
                component="span"
                sx={{
                  color: 'var(--booking-toolbar-text)',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  whiteSpace: 'nowrap',
                }}
              >
                Filter Manual Intervention
                {hideStaleManualIntervention && (
                  <Chip
                    key={chipAnimationKey}
                    variant="filled"
                    color="error"
                    size="small"
                    label={staleManualInterventionCount}
                    sx={{
                      transformOrigin: 'center',
                      animation: 'doorbellBuzz 360ms ease-in-out',
                      '@keyframes doorbellBuzz': {
                        '0%': {
                          transform: 'translateX(0) rotate(0deg)',
                        },
                        '12%': {
                          transform: 'translateX(-2px) rotate(-10deg)',
                        },
                        '24%': {
                          transform: 'translateX(2px) rotate(10deg)',
                        },
                        '36%': {
                          transform: 'translateX(-2px) rotate(-8deg)',
                        },
                        '48%': {
                          transform: 'translateX(2px) rotate(8deg)',
                        },
                        '60%': {
                          transform: 'translateX(-1px) rotate(-4deg)',
                        },
                        '72%': {
                          transform: 'translateX(1px) rotate(4deg)',
                        },
                        '100%': {
                          transform: 'translateX(0) rotate(0deg)',
                        },
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                      },
                    }}
                  />
                )}
              </Typography>
            }
            sx={{
              marginLeft: 0,
              flex: '0 1 auto',
              color: 'var(--booking-toolbar-text)',
              '.MuiSwitch-root': {
                marginRight: '4px',
              },
              '.MuiSwitch-switchBase': {
                color: 'var(--booking-toolbar-text-muted)',
              },
              '.MuiSwitch-track': {
                backgroundColor: 'var(--booking-toolbar-border)',
                opacity: 1,
              },
              '.MuiSwitch-switchBase.Mui-checked': {
                color: 'var(--booking-toolbar-text)',
              },
              '.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'var(--booking-toolbar-text)',
                opacity: 0.35,
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flex: '0 0 auto',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '36px',
            }}
          >
            <Tooltip
              title={
                copied ? 'Copied' : `Copy visible (${copyJobReferencesCount})`
              }
              placement="top"
            >
              <span>
                <IconButton
                  onClick={handleCopyJobReferences}
                  disabled={copyJobReferencesDisabled}
                  sx={{
                    color: 'var(--booking-toolbar-text)',
                    padding: '6px',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '&.Mui-disabled': {
                      color: 'var(--booking-toolbar-text-muted)',
                      opacity: 0.7,
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              flex: '1 1 320px',
              minWidth: { xs: '100%', md: '320px' },
              maxWidth: { xs: '100%', xl: '420px' },
              marginLeft: { xs: 0, md: 'auto' },
            }}
          >
            <Typography
              sx={{ color: 'var(--booking-toolbar-text)', fontSize: '14px' }}
            >
              Search:
            </Typography>
            <Input
              key="bookingSearch"
              id="bookingSearch"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              type={'text'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    edge="end"
                    onClick={() => setSearchText('')}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              }
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              placeholder="Search"
              fullWidth
              sx={{
                color: 'var(--booking-toolbar-text)',
                backgroundColor: 'var(--booking-toolbar-surface)',
                border: 'none',
                borderRadius: '4px 4px 0 0',
                '& .MuiSvgIcon-root': {
                  color: 'var(--booking-toolbar-text)',
                },
                '& .MuiIconButton-root': {
                  color: 'var(--booking-toolbar-text)',
                },
                '& input::placeholder': {
                  color: 'var(--booking-toolbar-text-muted)',
                  opacity: 1,
                },
                '&:before': {
                  borderBottomColor: 'var(--booking-toolbar-border)',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'var(--booking-toolbar-text)',
                },
                '&:after': {
                  borderBottomColor: 'var(--booking-toolbar-text)',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      {/* <Box sx={{ height: 4 }}>
        <LinearProgress sx={{ visibility: isLoading ? 'visible' : 'hidden' }} />
      </Box> */}
    </>
  )
}

export default Toolbar
